import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import LibraryBinding from './LibraryBinding';
import SectionSource from './SectionSource';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ComponentEditor from './ComponentEditor';

/**
 * One section block inside a page region. Owns the section's settings
 * (type, version, colors) and renders an editor row per component.
 */
export default function SectionCard({ section, catalog, brandId, page, onPatch, onDelete, onComponentChange }) {
  const [addAnchor, setAddAnchor] = React.useState(null);

  const components = useMemo(
    () => [...(section.componentData || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [section.componentData],
  );

  const typesForRegion = useMemo(() => {
    const all = catalog?.sections || [];
    if (section.region === 'header') return all.filter((s) => ['header', 'custom'].includes(s.type));
    if (section.region === 'footer') return all.filter((s) => ['footer', 'custom'].includes(s.type));
    return all.filter((s) => !['header', 'footer'].includes(s.type));
  }, [catalog, section.region]);

  const bound = section.source?.kind === 'library';
  const isOff = section.isVisible === false;

  const versionsForType = useMemo(
    () => (catalog?.sections || []).find((s) => s.type === section.type)?.versions || ['v1'],
    [catalog, section.type],
  );

  return (
    <Box
      sx={{
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        p: '14px',
        mb: '10px',
      }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='10px' gap='10px' flexWrap='wrap'>
        <Stack direction='row' spacing='8px' alignItems='center' flexWrap='wrap'>
          <Box color='text.secondary' title='Drag to reorder' sx={{ cursor: 'grab', display: 'flex' }}>
            <DragIndicatorIcon fontSize='small' />
          </Box>
          <Chip size='small' color='secondary' label={section.region} />
          <Typography fontWeight='600' variant='body2'>
            {section.name || section.type}
          </Typography>
          {isOff && <Chip size='small' variant='outlined' label='Off' />}
        </Stack>
        <Stack direction='row' alignItems='center' spacing='4px'>
          {/* Off hides the section from the live site and keeps everything —
              for a library section the content was never here to lose, and
              for a static one the components stay on the record. It is the
              honest alternative to deleting a section to "try without it". */}
          <Tooltip title={isOff ? 'Hidden from your live site' : 'Showing on your live site'}>
            <Switch
              size='small'
              checked={!isOff}
              onChange={(e) => onPatch({ isVisible: e.target.checked })}
            />
          </Tooltip>
          <IconButton size='small' color='error' onClick={onDelete}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing='10px'>
        <FormControl size='small' fullWidth>
          <InputLabel>Type</InputLabel>
          <Select
            label='Type'
            value={section.type}
            onChange={(e) => onPatch({ type: e.target.value, version: 'v1' })}
          >
            {typesForRegion.map((s) => (
              <MenuItem key={s.type} value={s.type}>{s.type}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' fullWidth>
          <InputLabel>Version</InputLabel>
          <Select
            label='Version'
            value={section.version || 'v1'}
            onChange={(e) => onPatch({ version: e.target.value })}
          >
            {versionsForType.map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box display='flex' flexDirection='column'>
          <Typography variant='caption' color='text.secondary' mb='4px'>Background</Typography>
          <input
            type='color'
            value={section.backgroundColor && section.backgroundColor !== 'transparent' ? section.backgroundColor : '#ffffff'}
            onChange={(e) => onPatch({ backgroundColor: e.target.value })}
            style={{ width: 50, height: 38, padding: 2, border: 'none', background: 'transparent' }}
          />
        </Box>

        <Box display='flex' flexDirection='column'>
          <Typography variant='caption' color='text.secondary' mb='4px'>Text</Typography>
          <input
            type='color'
            value={section.textColor || '#111827'}
            onChange={(e) => onPatch({ textColor: e.target.value })}
            style={{ width: 50, height: 38, padding: 2, border: 'none', background: 'transparent' }}
          />
        </Box>
      </Stack>

      {/* Offered on every section, not only on one that was created as a
          library section. The choice used to be made once in the Add dialog
          and could never be revisited, so a blog section from a template was
          typed-in cards forever. */}
      <SectionSource source={section.source} onPatch={onPatch} brandId={brandId} />

      {/* A library section has nothing of its own to edit — the words live in
          the library. Showing the component editor here would invite typing a
          team member into a page and wondering why it appeared nowhere else. */}
      {bound && (
        <Box mt='14px'>
          <LibraryBinding source={section.source} onPatch={onPatch} brandId={brandId} />
        </Box>
      )}

      {/* Offered only on a detail page, because anywhere else there is no
          entry for a section to carry and the choice would be meaningless. */}
      {page?.detailOf && !bound && (
        <Box mt='12px'>
          <FormControlLabel
            control={
              <Switch
                size='small'
                checked={!!section.showDetailEntry}
                onChange={(e) => onPatch({ showDetailEntry: e.target.checked })}
              />
            }
            label={<Typography variant='body2'>Show the blog post / entry here</Typography>}
          />
        </Box>
      )}

      {!bound && (
      <Box mt='14px' pt='12px' sx={{ borderTop: '1px dashed', borderColor: 'divider' }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' mb='8px'>
          <Typography
            variant='caption'
            fontWeight='700'
            color='text.secondary'
            textTransform='uppercase'
          >
            Components inside this section
          </Typography>
          <Button
            size='small'
            variant='outlined'
            startIcon={<AddIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setAddAnchor(e.currentTarget)}
          >
            Add component
          </Button>
          <Menu
            anchorEl={addAnchor}
            open={Boolean(addAnchor)}
            onClose={() => setAddAnchor(null)}
            PaperProps={{ sx: { maxHeight: 280 } }}
          >
            {(catalog?.components || []).map((c) => (
              <MenuItem
                key={c.type}
                onClick={() => {
                  onComponentChange?.({ action: 'add', section_id: section._id, type: c.type });
                  setAddAnchor(null);
                }}
              >
                {c.type}
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        {components.length === 0 && (
          <Typography variant='caption' color='text.secondary' fontStyle='italic'>
            No components yet. Add one above.
          </Typography>
        )}
        {components.map((comp) => (
          <ComponentEditor
            key={comp._id}
            component={comp}
            catalog={catalog}
            brandId={brandId}
            onPatch={(patch) => onComponentChange?.({ action: 'patch', component_id: comp._id, patch })}
            onDelete={() => onComponentChange?.({ action: 'delete', component_id: comp._id })}
          />
        ))}
      </Box>
      )}
    </Box>
  );
}
